import { useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/auth/auth-context';
import { persistLang } from '@/i18n/i18n';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Icon, type IconName } from '@/components/icon';
import { MfaCard } from '@/components/settings/mfa-card';

/**
 * `/settings` (admin) — Settings v1.
 *
 * 2026 design language: an icon-led page header over a stack of kit Cards.
 * Every value shown is real:
 *
 *   • Préférences — a language switcher (fr/en) built on the kit Radix
 *     `Select`. It persists to the SAME localStorage key the app already
 *     uses (`STORAGE_KEY` via `persistLang`) and calls `i18n.changeLanguage`,
 *     so the choice survives reload AND re-renders the app live. Persisting
 *     synchronously *before* `changeLanguage` makes `hasExplicitLangPreference`
 *     true immediately, closing the window where the next `/auth/me` could
 *     revert an explicit switch (see i18n.ts §6).
 *
 *   • Compte — read-only fields straight off the `/auth/me` response
 *     (`UserSummary`: display_name, username, locale, roles) via `useAuth`.
 *
 *   • À propos — application name (`common:app_name`) + an API-connection
 *     indicator derived from the real auth state machine (`status`). No
 *     fabricated build version / tenant rows: the app exposes no client-side
 *     build version, and `MeResponse` carries no tenant — so those rows are
 *     intentionally omitted rather than invented.
 *
 * Note: the topbar user-menu carries its own native-`<select>` locale switcher
 * (testid `locale-switcher`); this page uses a DISTINCT testid
 * (`settings-language-select`) so neither shadows the other in tests.
 */
export function SettingsPage(): JSX.Element {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const { status, currentUser } = useAuth();

  const currentLang: 'fr' | 'en' = i18n.language === 'en' ? 'en' : 'fr';

  const handleLanguageChange = useCallback(
    (next: string): void => {
      if (next !== 'fr' && next !== 'en') return;
      // Persist FIRST (synchronously) so an explicit preference is recorded
      // before the async re-render — then flip the live language.
      persistLang(next);
      void i18n.changeLanguage(next);
    },
    [i18n],
  );

  const apiState: { labelKey: string; variant: 'success' | 'neutral' | 'danger'; icon: IconName } =
    status === 'authenticated'
      ? { labelKey: 'system.api_operational', variant: 'success', icon: 'wifi' }
      : status === 'loading'
        ? { labelKey: 'system.api_checking', variant: 'neutral', icon: 'refresh-cw' }
        : { labelKey: 'system.api_unavailable', variant: 'danger', icon: 'wifi-off' };

  return (
    <div className="flex flex-col gap-6" data-testid="settings-page">
      {/* Icon-led page header (2026 detail-page pattern). */}
      <header className="flex items-start gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-border bg-muted text-muted-foreground">
          <Icon name="settings" size={24} aria-hidden />
        </span>
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('page_title')}</h1>
          <p className="text-sm text-muted-foreground">{t('page_subtitle')}</p>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* --- Préférences : language switcher (the load-bearing piece) --- */}
        <Card data-testid="settings-preferences-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Icon name="languages" size={18} aria-hidden />
              {t('preferences.title')}
            </CardTitle>
            <CardDescription>{t('preferences.description')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2">
            <label
              htmlFor="settings-language-select"
              className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
            >
              {t('preferences.language_label')}
            </label>
            <Select value={currentLang} onValueChange={handleLanguageChange}>
              <SelectTrigger
                id="settings-language-select"
                data-testid="settings-language-select"
                aria-label={t('preferences.language_aria')}
                className="w-full sm:w-64"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fr" data-testid="settings-language-option-fr">
                  {t('common:language_french')}
                </SelectItem>
                <SelectItem value="en" data-testid="settings-language-option-en">
                  {t('common:language_english')}
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{t('preferences.language_hint')}</p>
          </CardContent>
        </Card>

        {/* --- Compte : read-only /auth/me fields --- */}
        <Card data-testid="settings-account-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Icon name="user" size={18} aria-hidden />
              {t('account.title')}
            </CardTitle>
            <CardDescription>{t('account.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            {currentUser ? (
              <dl className="flex flex-col divide-y divide-border">
                <InfoRow icon="user" label={t('account.display_name')}>
                  <span data-testid="settings-account-display-name">{currentUser.display_name}</span>
                </InfoRow>
                <InfoRow icon="at-sign" label={t('account.username')}>
                  <span className="font-mono" data-testid="settings-account-username">
                    {currentUser.username}
                  </span>
                </InfoRow>
                <InfoRow icon="languages" label={t('account.language')}>
                  <span data-testid="settings-account-locale">
                    {currentUser.locale === 'en'
                      ? t('common:language_english')
                      : t('common:language_french')}
                  </span>
                </InfoRow>
                <InfoRow icon="shield" label={t('account.roles')}>
                  <ul className="flex flex-wrap gap-1.5" data-testid="settings-account-roles">
                    {currentUser.roles.map((role) => (
                      <li key={role}>
                        <Badge variant="primary">
                          {t(`account.role_${role}`, { defaultValue: role })}
                        </Badge>
                      </li>
                    ))}
                  </ul>
                </InfoRow>
              </dl>
            ) : (
              <p className="text-sm text-muted-foreground">{t('account.unavailable')}</p>
            )}
          </CardContent>
        </Card>

        {/* --- À propos : real application + connection info only --- */}
        <Card data-testid="settings-system-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Icon name="info" size={18} aria-hidden />
              {t('system.title')}
            </CardTitle>
            <CardDescription>{t('system.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <dl className="flex flex-col divide-y divide-border">
              <InfoRow icon="monitor" label={t('system.application')}>
                <span data-testid="settings-system-app-name">{t('common:app_name')}</span>
              </InfoRow>
              <InfoRow icon={apiState.icon} label={t('system.api_status')}>
                <Badge
                  variant={apiState.variant}
                  icon={apiState.icon}
                  data-testid="settings-system-api-status"
                >
                  {t(apiState.labelKey)}
                </Badge>
              </InfoRow>
            </dl>
          </CardContent>
        </Card>

        {/* --- Sécurité : TOTP two-factor (P7) --- */}
        <MfaCard />
      </div>
    </div>
  );
}

/** Icon-led read-only row: a tinted icon chip, an uppercase label, a value. */
function InfoRow({
  icon,
  label,
  children,
}: {
  icon: IconName;
  label: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <Icon name={icon} size={16} aria-hidden />
      </span>
      <div className="flex flex-col gap-1">
        <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {label}
        </dt>
        <dd className="text-sm">{children}</dd>
      </div>
    </div>
  );
}

export default SettingsPage;
